import surveyDefinitionJson from "../config/experiment-post-survey.ja.json";

export interface ExperimentSurveyItem {
  key: string;
  label: string;
  required: boolean;
}

export interface ExperimentSurveyTextItem extends ExperimentSurveyItem {
  placeholder: string;
  helpText: string;
  position: "beforeLikert" | "afterLikert";
}

export interface ExperimentSurveyShortTextItem extends ExperimentSurveyItem {
  placeholder: string;
}

export interface ExperimentSurveySingleChoiceItem extends ExperimentSurveyItem {
  options: Array<{ value: string; label: string }>;
}

export interface ExperimentSurveyDefinition {
  id: string;
  version: string;
  locale: string;
  title: string;
  subtitle: string;
  scale: {
    min: number;
    max: number;
    labels: string[];
  };
  shortTextItems: ExperimentSurveyShortTextItem[];
  singleChoiceItems: ExperimentSurveySingleChoiceItem[];
  likertTitle: string;
  likertItems: ExperimentSurveyItem[];
  textItems: ExperimentSurveyTextItem[];
}

export interface ExperimentSurveyAnswers {
  shortText: Record<string, string>;
  singleChoice: Record<string, string>;
  ratings: Record<string, number>;
  freeText: Record<string, string>;
}

export interface ExperimentSurveyResponsePayload extends ExperimentSurveyAnswers {
  surveyId: string;
  surveyVersion: string;
  locale: string;
  submittedAt: string;
}

export const EXPERIMENT_POST_SURVEY = surveyDefinitionJson as ExperimentSurveyDefinition;

export function createEmptySurveyAnswers(
  definition: ExperimentSurveyDefinition = EXPERIMENT_POST_SURVEY,
): ExperimentSurveyAnswers {
  return {
    shortText: Object.fromEntries(definition.shortTextItems.map((item) => [item.key, ""])),
    singleChoice: {},
    ratings: {},
    freeText: Object.fromEntries(definition.textItems.map((item) => [item.key, ""])),
  };
}

export function normalizeSurveyAnswers(
  value: unknown,
  definition: ExperimentSurveyDefinition = EXPERIMENT_POST_SURVEY,
): ExperimentSurveyAnswers {
  const empty = createEmptySurveyAnswers(definition);
  if (!isRecord(value)) return empty;

  const ratingsSource = isRecord(value.ratings) ? value.ratings : {};
  const freeTextSource = isRecord(value.freeText) ? value.freeText : {};
  const shortTextSource = isRecord(value.shortText) ? value.shortText : {};
  const singleChoiceSource = isRecord(value.singleChoice) ? value.singleChoice : {};

  const shortText: Record<string, string> = { ...empty.shortText };
  for (const item of definition.shortTextItems) {
    const raw = shortTextSource[item.key];
    if (typeof raw === "string") shortText[item.key] = raw;
  }

  const singleChoice: Record<string, string> = {};
  for (const item of definition.singleChoiceItems) {
    const raw = singleChoiceSource[item.key];
    if (typeof raw === "string" && item.options.some((option) => option.value === raw)) {
      singleChoice[item.key] = raw;
    }
  }

  const ratings: Record<string, number> = {};
  for (const item of definition.likertItems) {
    const raw = ratingsSource[item.key];
    if (
      typeof raw === "number" &&
      Number.isInteger(raw) &&
      raw >= definition.scale.min &&
      raw <= definition.scale.max
    ) {
      ratings[item.key] = raw;
    }
  }

  const freeText: Record<string, string> = { ...empty.freeText };
  for (const item of definition.textItems) {
    const raw = freeTextSource[item.key];
    if (typeof raw === "string") freeText[item.key] = raw;
  }

  return { shortText, singleChoice, ratings, freeText };
}

export function isCurrentSurveyResponse(
  value: unknown,
  definition: ExperimentSurveyDefinition = EXPERIMENT_POST_SURVEY,
): boolean {
  return isRecord(value) &&
    value.surveyId === definition.id &&
    value.surveyVersion === definition.version;
}

export function countMissingRequiredSurveyAnswers(
  answers: ExperimentSurveyAnswers,
  definition: ExperimentSurveyDefinition = EXPERIMENT_POST_SURVEY,
): number {
  const missingShortText = definition.shortTextItems.filter(
    (item) => item.required && !answers.shortText[item.key]?.trim(),
  ).length;
  const missingSingleChoice = definition.singleChoiceItems.filter(
    (item) => item.required && !answers.singleChoice[item.key],
  ).length;
  const missingRatings = definition.likertItems.filter(
    (item) => item.required && answers.ratings[item.key] === undefined,
  ).length;
  const missingText = definition.textItems.filter(
    (item) => item.required && !answers.freeText[item.key]?.trim(),
  ).length;
  return missingShortText + missingSingleChoice + missingRatings + missingText;
}

export function buildSurveyResponsePayload(
  answers: ExperimentSurveyAnswers,
  submittedAt: string,
  definition: ExperimentSurveyDefinition = EXPERIMENT_POST_SURVEY,
): ExperimentSurveyResponsePayload {
  return {
    surveyId: definition.id,
    surveyVersion: definition.version,
    locale: definition.locale,
    shortText: answers.shortText,
    singleChoice: answers.singleChoice,
    ratings: answers.ratings,
    freeText: answers.freeText,
    submittedAt,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
